/**
 * Obstacle Store
 * Zustand state management for obstacle editor interactions
 */

import { create } from 'zustand'
import { devtools } from 'zustand/middleware'
import type { ObstaclePreset, ObstacleType, ObstacleMobility, ObstacleShape } from '@/types/obstacle'

/** Form values for the creation ghost + mutation (before saving to DB) */
export interface CreateObstacleDefaults {
  name: string
  obstacleType: ObstacleType
  mobility: ObstacleMobility
  shape: ObstacleShape
  width: number
  depth: number
  radius: number
  height: number
  elevation: number
  color: string
  opacity: number
  participatesInCollision: boolean
  clearanceBufferFt: number
}

interface ObstacleState {
  /** Currently selected obstacle ID */
  selectedObstacleId: string | null
  /** Currently hovered obstacle ID */
  hoveredObstacleId: string | null
  /** Whether user is in placing mode (adding new obstacle from preset) */
  isPlacingMode: boolean
  /** The preset being placed */
  placingPreset: ObstaclePreset | null
  /** Whether obstacle is being dragged */
  isDragging: boolean
  /** Cursor world position during placement (scene feet) */
  previewPosition: { x: number; z: number } | null
  /** Obstacle ID to focus camera on (set by panel, consumed by map) */
  focusObstacleId: string | null
  /** Controls 3D mesh + label visibility */
  obstaclesVisible: boolean
  /** True when the floating create form is open (view-mode placement) */
  isCreatingMode: boolean
  /** Target building for "Add Obstacle" (null = auto-detect on click) */
  creatingForBuildingId: string | null
  /** Form values for the ghost + creation mutation */
  creatingDefaults: CreateObstacleDefaults | null
}

interface ObstacleActions {
  selectObstacle: (id: string | null) => void
  setHovered: (id: string | null) => void
  startPlacing: (preset: ObstaclePreset) => void
  cancelPlacing: () => void
  setDragging: (dragging: boolean) => void
  setPreviewPosition: (pos: { x: number; z: number } | null) => void
  requestFocusOnObstacle: (id: string | null) => void
  clearSelection: () => void
  toggleObstaclesVisible: () => void
  startCreating: (buildingId: string | null) => void
  cancelCreating: () => void
  setCreatingDefaults: (defaults: CreateObstacleDefaults) => void
  reset: () => void
}

const DEFAULT_CREATING_DEFAULTS: CreateObstacleDefaults = {
  name: 'New Obstacle',
  obstacleType: 'column',
  mobility: 'static',
  shape: 'rectangle',
  width: 10,
  depth: 10,
  radius: 5,
  height: 6,
  elevation: 0,
  color: '#808080',
  opacity: 1,
  participatesInCollision: true,
  clearanceBufferFt: 1,
}

const initialState: ObstacleState = {
  selectedObstacleId: null,
  hoveredObstacleId: null,
  isPlacingMode: false,
  placingPreset: null,
  isDragging: false,
  previewPosition: null,
  focusObstacleId: null,
  obstaclesVisible: true,
  isCreatingMode: false,
  creatingForBuildingId: null,
  creatingDefaults: null,
}

export const useObstacleStore = create<ObstacleState & ObstacleActions>()(
  devtools(
    (set) => ({
      ...initialState,

      selectObstacle: (id) =>
        set({
          selectedObstacleId: id,
          isPlacingMode: false,
          placingPreset: null,
          isCreatingMode: false,
          creatingForBuildingId: null,
          creatingDefaults: null,
          previewPosition: null,
        }),

      setHovered: (id) => set({ hoveredObstacleId: id }),

      startPlacing: (preset) =>
        set({
          isPlacingMode: true,
          placingPreset: preset,
          selectedObstacleId: null,
        }),

      cancelPlacing: () =>
        set({ isPlacingMode: false, placingPreset: null, previewPosition: null }),

      setDragging: (dragging) => set({ isDragging: dragging }),

      setPreviewPosition: (pos) => set({ previewPosition: pos }),

      requestFocusOnObstacle: (id) => set({ focusObstacleId: id }),

      clearSelection: () =>
        set({ selectedObstacleId: null, hoveredObstacleId: null }),

      toggleObstaclesVisible: () =>
        set((state) => ({
          obstaclesVisible: !state.obstaclesVisible,
          // Clear selection when hiding
          ...(state.obstaclesVisible
            ? { selectedObstacleId: null, hoveredObstacleId: null, isCreatingMode: false, creatingDefaults: null, creatingForBuildingId: null, previewPosition: null }
            : {}),
        })),

      startCreating: (buildingId) => {
        set({
          isCreatingMode: true,
          creatingForBuildingId: buildingId,
          creatingDefaults: { ...DEFAULT_CREATING_DEFAULTS },
          selectedObstacleId: null,
          isPlacingMode: false,
          placingPreset: null,
        })
      },

      cancelCreating: () =>
        set({
          isCreatingMode: false,
          creatingForBuildingId: null,
          creatingDefaults: null,
          previewPosition: null,
        }),

      setCreatingDefaults: (defaults) =>
        set({ creatingDefaults: defaults }),

      // Preserve obstaclesVisible across resets
      reset: () => set((state) => ({ ...initialState, obstaclesVisible: state.obstaclesVisible })),
    }),
    { name: 'obstacle-store' }
  )
)
